import { useContext } from 'react';

import {
  useMutation,
  UseMutationOptions,
  UseMutationResult,
  useQueryClient,
} from '@tanstack/react-query';

import { createFacebookTrackingEvent } from '@/common/analytics/facebookPixel/createFacebookTrackingEvent';
import { HeaderContentQueryKey } from '@/common/hooks/useHeaderContent/useHeaderContentInfo';
import { AuthContext } from '@/contexts/auth/AuthContext';
import { AddToWishlistEvent, BfwUserContextService } from 'generatedBfw/static';

interface MutationArguments {
  externalId: number;
}

const key = 'add-to-wishlist';

export const useAddToWishlist = (
  options?:
    | Omit<UseMutationOptions<AddToWishlistEvent, Error, MutationArguments>, typeof key>
    | undefined,
): UseMutationResult<AddToWishlistEvent, Error, MutationArguments> => {
  const ctx = useContext(AuthContext);
  const queryClient = useQueryClient();
  return useMutation(
    [key],
    ({ externalId }) => {
      if (!ctx.fetchedUserInfo) {
        return Promise.reject('Cannot use useAddToWishlist before fetching user info');
      }

      return BfwUserContextService.wishlistAddSingleItem({
        itemCollectionId: externalId,
      });
    },
    {
      ...options,
      onSuccess: (data, variables, context) => {
        // update wishlist and cart numbers on header
        queryClient.invalidateQueries(HeaderContentQueryKey);

        data && createFacebookTrackingEvent('AddToWishlist', data.eventContent, data.eventId);
        options?.onSuccess && options?.onSuccess(data, variables, context);
      },
    },
  );
};
